import { useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams } from '../hooks/useUrlState';
import {
  api, AppConfig, InvoiceReportClient, InvoiceReportInvoice, InvoiceReportMonthPoint,
  InvoiceReportParent, InvoiceWorklogLine, WorklogIdDiff, WorklogIdDiffRow,
} from '../lib/api';
import { fmtCurrencyCents as fmtCurrency } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  SectionLabel, Card, CardHeader, MetricCard, Badge,
  Table, Th, Tr, Td, Spinner, EmptyState, SortableTh, ActionButton, UpdatingBadge,
} from '../components/ui';
import { InvoiceLink } from '../components/InvoiceLink';
import { CreditAvailableBadge } from '../components/CreditAvailableBadge';
import { useConfig, jiraWorklogUrl } from '../hooks/useConfig';
import { useTableSort } from '../hooks/useTableSort';
import { statusBadgeVariant } from './AdminEngagementBudgetPage';

// ─── Invoicing report ───────────────────────────────────────────────────────
// Three levels deep — clients (tempo accounts) → engagements → invoices. Each
// level rolls up billed / paid / overdue / write-off / credit. Expand a
// client to see its engagements + orphan invoices; expand an engagement to
// see its individual invoices.

const DEFAULTS = { search: '', filter: 'all', overdue: 'any' };

type FilterId = 'all' | 'overdue' | 'outstanding' | 'unapproved' | 'unapproved-with-deposit' | 'paid' | 'chargeover-mismatch';
type OverdueBucket = 'any' | '31' | '61' | '91';

/** "Not yet issued to a customer" — either the normalised lifecycle says
 *  so, or the raw_status carries ChargeOver's own label. Checking both
 *  makes the filter resilient to stale lifecycle values persisted before
 *  the unapproved-handling fix shipped. */
function isUnapproved(inv: InvoiceReportInvoice): boolean {
  if (inv.lifecycle === 'draft') return true;
  const s = (inv.raw_status ?? '').toLowerCase();
  return s.includes('unapprov') || s.includes('draft') || s === 'pending' || s === 'scheduled';
}

function isVoid(inv: InvoiceReportInvoice): boolean {
  if (inv.lifecycle === 'void') return true;
  const s = (inv.raw_status ?? '').toLowerCase();
  return s.includes('void') || s.includes('cancel') || s.includes('delet');
}

function isPaid(inv: InvoiceReportInvoice): boolean {
  if (inv.lifecycle === 'paid') return true;
  const s = (inv.raw_status ?? '').toLowerCase();
  return s.includes('paid');
}

/** Shared predicate for the "unapproved with outstanding deposit" filter
 *  — a draft invoice for a customer that has at least one pending deposit
 *  in flight. Same condition as the per-row 🕒 badge. */
function invHasUnapprovedDeposit(inv: InvoiceReportInvoice): boolean {
  return isUnapproved(inv) && inv.customer_pending_deposit > 0.01;
}

/** A verify pass found ChargeOver's stored worklog list and ORP's disagree on
 *  at least one weekly line.
 *
 *  Reads "known to disagree", NOT "not known to agree": invoices whose detail
 *  was never verified — or never captured at all — are excluded, because a
 *  reconciliation worklist padded with unverified invoices is not a worklist.
 *  Run the backfill's verify pass to populate it. */
function invHasChargeoverMismatch(inv: InvoiceReportInvoice): boolean {
  return inv.has_chargeover_discrepancy;
}

function clientHasChargeoverMismatch(c: InvoiceReportClient): boolean {
  return c.engagements.some((e) => e.invoices.some(invHasChargeoverMismatch))
    || c.orphan_invoices.some(invHasChargeoverMismatch);
}

/** Per-invoice predicate that mirrors the client-level tab semantics, so
 *  expanding a filtered client only shows the invoices that actually match
 *  the status (rather than every invoice the client ever had). */
function invoiceMatchesFilter(
  inv: InvoiceReportInvoice,
  filter: FilterId,
  overdueBucket: OverdueBucket,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'unapproved')  return isUnapproved(inv);
  // Unapproved drafts on a customer who has at least one pending deposit
  // invoice — finance's "fix this before sending" worklist. Same predicate
  // as the per-row 🕒 badge, surfaced as a top-level filter so the
  // operator can see only the rows that need action.
  if (filter === 'unapproved-with-deposit') {
    return isUnapproved(inv) && inv.customer_pending_deposit > 0.01;
  }
  if (filter === 'paid')        return isPaid(inv) && !isUnapproved(inv);
  if (filter === 'chargeover-mismatch') return invHasChargeoverMismatch(inv);
  if (filter === 'outstanding') {
    // Outstanding = issued, not paid, has a balance. Unapproved invoices
    // haven't been issued yet and therefore are NEVER outstanding.
    // balance_net, not balance_amount: an invoice a deposit already settled is
    // not something to chase, and listing it here would contradict the client
    // total, which nets the drawdown out.
    if (isUnapproved(inv) || isVoid(inv) || isPaid(inv)) return false;
    return inv.balance_net > 0.01;
  }
  if (filter === 'overdue') {
    // Same exclusion: unapproved/void/paid never count as overdue.
    if (isUnapproved(inv) || isVoid(inv) || isPaid(inv)) return false;
    const d = inv.days_past_due;
    if (d == null || d <= 0) return false;
    if (overdueBucket === '31' && d < 31) return false;
    if (overdueBucket === '61' && d < 61) return false;
    if (overdueBucket === '91' && d < 91) return false;
    return true;
  }
  return true;
}

/** YYYY-MM → "Mar 2026" style label. */
function monthLabel(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-').map((s) => parseInt(s, 10));
  if (!y || !m) return yyyyMm;
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

/** Tiny 6-month bar chart — pure SVG, no dependencies. The last bar is the
 *  "current" month (most recent complete) and is highlighted; earlier bars
 *  are muted context. Heights scale to the series max so the chart always
 *  fills its box even when the values are small. */
function Sparkline({ data }: { data: InvoiceReportMonthPoint[] }) {
  const values = data.map((d) => d.value);
  const max = Math.max(...values, 1);
  const barWidth = 5;
  const gap = 2;
  const height = 22;
  const width = data.length * (barWidth + gap) - gap;
  return (
    <svg width={width} height={height} aria-hidden style={{ display: 'block' }}>
      {data.map((d, i) => {
        const h = d.value > 0 ? Math.max(2, (d.value / max) * height) : 1;
        const isLast = i === data.length - 1;
        return (
          <rect
            key={d.month}
            x={i * (barWidth + gap)}
            y={height - h}
            width={barWidth}
            height={h}
            rx={1}
            fill={isLast ? 'var(--color-blue)' : 'var(--color-border-strong)'}
          >
            <title>{`${monthLabel(d.month)}: ${fmtCurrency(d.value)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** Tiny bar chart for the overdue age buckets (1-30 / 31-60 / 61-90 / 91+).
 *  Bars get redder as the bucket ages, to reinforce the risk gradient. */
function OverdueBucketChart({
  buckets,
}: {
  buckets: Array<{ label: string; value: number; color: string }>;
}) {
  const max = Math.max(...buckets.map((b) => b.value), 1);
  const barWidth = 8;
  const gap = 3;
  const height = 22;
  const width = buckets.length * (barWidth + gap) - gap;
  return (
    <svg width={width} height={height} aria-hidden style={{ display: 'block' }}>
      {buckets.map((b, i) => {
        const h = b.value > 0 ? Math.max(2, (b.value / max) * height) : 1;
        return (
          <rect
            key={b.label}
            x={i * (barWidth + gap)}
            y={height - h}
            width={barWidth}
            height={h}
            rx={1}
            fill={b.value > 0 ? b.color : 'var(--color-border-strong)'}
          >
            <title>{`${b.label}: ${fmtCurrency(b.value)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** Overdue metric card with an embedded age-bucket bar chart. Shares the
 *  visual weight of MonthlyMetric so the top row stays consistent. */
function OverdueMetric({
  overdue, buckets,
}: {
  overdue: number;
  buckets: Array<{ label: string; value: number; color: string }>;
}) {
  const worst = [...buckets].reverse().find((b) => b.value > 0);
  const anyOverdue = overdue > 0;
  return (
    <div style={{
      position: 'relative',
      padding: '10px 12px',
      border: '0.5px solid var(--color-border)',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--color-surface)',
      overflow: 'hidden',
    }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Overdue
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: anyOverdue ? 'var(--color-red-text)' : undefined }}>
        {fmtCurrency(overdue)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
        {anyOverdue
          ? (worst ? `Oldest: ${worst.label}` : 'Past due, unpaid')
          : 'Nothing overdue'}
      </div>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
        {anyOverdue ? 'By age: 1-30 / 31-60 / 61-90 / 91+' : '\u00A0'}
      </div>
      <div style={{ position: 'absolute', right: 10, bottom: 10 }}>
        <OverdueBucketChart buckets={buckets} />
      </div>
    </div>
  );
}

/** Monthly-comparison metric card. Renders the current-month value with a
 *  delta chip vs previous month and an all-time annotation. Delta shows
 *  as percentage when previous > 0; otherwise as an absolute dollar delta
 *  (so the first-ever month of data doesn't divide by zero). A 6-month
 *  sparkline anchors the lower-right for at-a-glance trend. */
function MonthlyMetric({
  label, current, previous, previousLabel, allTime, history,
}: {
  label: string;
  current: number;
  previous: number;
  previousLabel: string;
  allTime: number;
  history: InvoiceReportMonthPoint[];
}) {
  const delta = current - previous;
  const pct = previous !== 0 ? Math.round((delta / previous) * 100) : null;
  const up   = delta > 0;
  const flat = Math.abs(delta) < 0.01;
  const color = flat ? 'var(--color-text-muted)' : up ? 'var(--color-green)' : 'var(--color-red-text)';
  const arrow = flat ? '→' : up ? '▲' : '▼';
  const chip  = pct != null
    ? `${arrow} ${Math.abs(pct)}%`
    : up ? `${arrow} ${fmtCurrency(Math.abs(delta))}` : `${arrow} ${fmtCurrency(Math.abs(delta))}`;
  return (
    <div style={{
      position: 'relative',
      padding: '10px 12px',
      border: '0.5px solid var(--color-border)',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--color-surface)',
      overflow: 'hidden',
    }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
        {fmtCurrency(current)}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4, fontSize: 11 }}>
        <span style={{ color, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{chip}</span>
        <span style={{ color: 'var(--color-text-muted)' }}>
          vs {fmtCurrency(previous)} in {previousLabel}
        </span>
      </div>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
        All-time: {fmtCurrency(allTime)}
      </div>
      <div style={{ position: 'absolute', right: 10, bottom: 10 }}>
        <Sparkline data={history} />
      </div>
    </div>
  );
}

/** Highest days_past_due across every invoice under this client (engagement
 *  invoices + orphan invoices). Used to bucket the Overdue filter. */
function maxDaysPastDue(client: InvoiceReportClient): number {
  let max = 0;
  for (const inv of client.orphan_invoices) {
    if (inv.days_past_due != null && inv.days_past_due > max) max = inv.days_past_due;
  }
  for (const eng of client.engagements) {
    for (const inv of eng.invoices) {
      if (inv.days_past_due != null && inv.days_past_due > max) max = inv.days_past_due;
    }
  }
  return max;
}

export default function AdminInvoicingPage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(() => api.admin.invoicing());
  const [params, setParams] = useUrlParams(DEFAULTS);
  const search = params.search;
  const filter = params.filter as FilterId;
  const overdueBucket = params.overdue as OverdueBucket;

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const clientMatches = (c: InvoiceReportClient): boolean => {
      if (filter === 'outstanding' && c.outstanding_balance <= 0) return false;
      if (filter === 'unapproved' && c.draft_total <= 0) return false;
      if (filter === 'unapproved-with-deposit') {
        // Client matches when it has at least one draft invoice whose
        // customer carries a pending deposit. The per-invoice flag
        // is set per-customer, so we just probe any draft on the
        // client and check it.
        const hasMatch =
          c.engagements.some((e) => e.invoices.some(invHasUnapprovedDeposit))
          || c.orphan_invoices.some(invHasUnapprovedDeposit);
        if (!hasMatch) return false;
      }
      if (filter === 'paid' && c.paid_total <= 0) return false;
      if (filter === 'chargeover-mismatch' && !clientHasChargeoverMismatch(c)) return false;
      if (filter === 'overdue') {
        if (c.overdue_balance <= 0) return false;
        const maxDays = maxDaysPastDue(c);
        if (overdueBucket === '31' && maxDays < 31) return false;
        if (overdueBucket === '61' && maxDays < 61) return false;
        if (overdueBucket === '91' && maxDays < 91) return false;
      }
      if (!needle) return true;
      if ((c.chargeover_display_name ?? '').toLowerCase().includes(needle)) return true;
      if ((c.tempo_account_name ?? '').toLowerCase().includes(needle)) return true;
      if ((c.tempo_account_id ?? '').toLowerCase().includes(needle)) return true;
      return c.engagements.some((e) =>
        e.engagement_jira_issue_key.toLowerCase().includes(needle)
        || e.engagement_summary.toLowerCase().includes(needle),
      );
    };
    // Parent also matches if the parent's own name contains the needle —
    // the user typing a client name should find the parent block even if
    // no individual child row would match on its own.
    const parentNameMatches = (p: InvoiceReportParent): boolean =>
      !!needle && p.name.toLowerCase().includes(needle);
    const out: InvoiceReportParent[] = [];
    for (const p of data.parents) {
      const parentHit = parentNameMatches(p);
      const clients = p.clients.filter((c) => parentHit || clientMatches(c));
      if (clients.length === 0) continue;
      out.push({ ...p, clients });
    }
    return out;
  }, [data, search, filter, overdueBucket]);

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;

  const t = data.totals;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Invoicing</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isFetching && <UpdatingBadge />}
          <ActionButton onClick={() => setParams(DEFAULTS)} variant="default">Clear filters</ActionButton>
          <ActionButton
            onClick={() => data && downloadInvoicingCsv(data.parents)}
            variant="default"
            disabled={!data}
          >
            Export CSV
          </ActionButton>
          <ActionButton onClick={refetch} variant="default">Refresh</ActionButton>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MonthlyMetric
          label={`Billed · ${monthLabel(data.monthly.current_month)}`}
          current={data.monthly.billed.current}
          previous={data.monthly.billed.previous}
          previousLabel={monthLabel(data.monthly.previous_month)}
          allTime={t.billed_total}
          history={data.monthly.billed.history}
        />
        <MonthlyMetric
          label={`Collected · ${monthLabel(data.monthly.current_month)}`}
          current={data.monthly.paid.current}
          previous={data.monthly.paid.previous}
          previousLabel={monthLabel(data.monthly.previous_month)}
          allTime={t.paid_total}
          history={data.monthly.paid.history}
        />
        <MonthlyMetric
          label={`Deposits collected · ${monthLabel(data.monthly.current_month)}`}
          current={data.monthly.deposits_collected.current}
          previous={data.monthly.deposits_collected.previous}
          previousLabel={monthLabel(data.monthly.previous_month)}
          allTime={t.deposit_total_issued}
          history={data.monthly.deposits_collected.history}
        />
        <MetricCard
          label="Outstanding"
          value={fmtCurrency(t.outstanding_balance)}
          accent={t.outstanding_balance > 0 ? undefined : 'green'}
          sub={
            t.overdue_balance > 0
              ? `${fmtCurrency(t.overdue_balance)} overdue`
              : t.outstanding_balance > 0 ? 'All current' : 'Nothing outstanding'
          }
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginTop: 10 }}>
        <OverdueMetric
          overdue={t.overdue_balance}
          buckets={[
            { label: '1-30d',  value: t.overdue_1_30_balance,    color: 'var(--color-amber)' },
            { label: '31-60d', value: t.overdue_31_60_balance,   color: 'var(--color-amber)' },
            { label: '61-90d', value: t.overdue_61_90_balance,   color: 'var(--color-red)' },
            { label: '91+d',   value: t.overdue_91_plus_balance, color: 'var(--color-red)' },
          ]}
        />
        <MetricCard label="Unapproved invoices" value={fmtCurrency(t.draft_total)} sub="Not yet issued to customer" />
        <MetricCard
          label="Deposits remaining"
          value={fmtCurrency(t.deposit_remaining)}
          sub={`${fmtCurrency(t.deposit_total_issued)} issued all-time`}
        />
        <MetricCard
          label="Credits remaining"
          value={fmtCurrency(t.credit_remaining)}
          sub={`${fmtCurrency(t.credit_total_issued)} issued all-time`}
        />
        <MetricCard
          label="Expected to collect"
          value={fmtCurrency(t.expected_to_collect)}
          accent="blue"
          sub="Unapproved − deposits − credits"
        />
      </div>

      <Card>
        <CardHeader>
          {([
            { id: 'all' as FilterId,         label: 'All clients',  count: data.clients.length },
            { id: 'unapproved' as FilterId,  label: 'Unapproved',   count: data.clients.filter((c) => c.draft_total > 0).length },
            {
              id: 'unapproved-with-deposit' as FilterId,
              label: 'Unapproved · outstanding deposit',
              count: data.clients.filter((c) =>
                c.engagements.some((e) => e.invoices.some(invHasUnapprovedDeposit))
                || c.orphan_invoices.some(invHasUnapprovedDeposit)
              ).length,
            },
            { id: 'outstanding' as FilterId, label: 'Outstanding',  count: data.clients.filter((c) => c.outstanding_balance > 0).length },
            { id: 'overdue' as FilterId,     label: 'Overdue',      count: data.clients.filter((c) => c.overdue_balance > 0).length },
            { id: 'paid' as FilterId,        label: 'Paid',         count: data.clients.filter((c) => c.paid_total > 0).length },
            {
              id: 'chargeover-mismatch' as FilterId,
              label: 'ChargeOver mismatch',
              count: data.clients.filter(clientHasChargeoverMismatch).length,
            },
          ]).map((f) => (
            <button
              key={f.id}
              onClick={() => setParams({ filter: f.id })}
              style={{
                background: 'none', border: 'none',
                borderBottom: filter === f.id ? '2px solid var(--color-blue)' : '2px solid transparent',
                padding: '8px 16px',
                fontSize: 12, fontWeight: 500,
                color: filter === f.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {f.label} <Badge variant={
                f.id === 'overdue' ? (f.count === 0 ? 'green' : 'amber')
                : f.id === 'unapproved-with-deposit' ? (f.count === 0 ? 'green' : 'amber')
                // Zero here is genuinely good news OR simply "never verified" —
                // the empty state below draws that distinction.
                : f.id === 'chargeover-mismatch' ? (f.count === 0 ? 'green' : 'red')
                : f.id === 'paid' ? 'green'
                : 'gray'
              }>{f.count}</Badge>
            </button>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Filter by client or engagement…"
              value={search}
              onChange={(e) => setParams({ search: e.target.value })}
              style={{
                fontSize: 11, padding: '4px 8px', width: 260,
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
              }}
            />
          </span>
        </CardHeader>

        {/* Overdue sub-chips: only rendered when the Overdue tab is active. */}
        {filter === 'overdue' && (
          <div style={{
            display: 'flex', gap: 6, padding: '8px 16px',
            borderBottom: '0.5px solid var(--color-border)',
            background: 'var(--color-bg)',
          }}>
            {([
              { id: 'any' as OverdueBucket, label: 'All' },
              { id: '31'  as OverdueBucket, label: '31+ days' },
              { id: '61'  as OverdueBucket, label: '61+ days' },
              { id: '91'  as OverdueBucket, label: '91+ days' },
            ]).map((b) => (
              <button
                key={b.id}
                onClick={() => setParams({ overdue: b.id })}
                style={{
                  fontSize: 11, padding: '4px 10px',
                  borderRadius: 4,
                  border: overdueBucket === b.id ? '0.5px solid var(--color-red)' : '0.5px solid var(--color-border-strong)',
                  background: overdueBucket === b.id ? 'var(--color-red-bg)' : 'var(--color-surface)',
                  color: overdueBucket === b.id ? 'var(--color-red-text)' : 'var(--color-text-muted)',
                  cursor: 'pointer',
                }}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState message={
            search.trim() ? 'No matches.'
            // An empty mismatch list is ambiguous: nothing disagrees, or
            // nothing has been checked. Saying which saves a false all-clear.
            : filter === 'chargeover-mismatch'
              ? 'No invoices are known to disagree with ChargeOver. Only invoices whose detail has been verified are checked — run the invoice worklog detail backfill on Admin → Data sync to widen the coverage.'
            : 'No clients with invoices in your scope.'
          } />
        ) : (
          <ParentTable parents={filtered} filter={filter} overdueBucket={overdueBucket} />
        )}
      </Card>
    </>
  );
}

// Concatenate Jira engagement keys attached to a ChargeOver child customer.
// Most children have exactly one engagement; in the rare multi-engagement
// case we show every key comma-separated for context.
function engagementKeys(client: InvoiceReportClient): string {
  const keys = client.engagements.map((e) => e.engagement_jira_issue_key).filter(Boolean);
  return keys.join(', ');
}

function downloadInvoicingCsv(parents: InvoiceReportParent[]) {
  const rows: Array<Array<unknown>> = [[
    'Client (parent)', 'Engagement (child)', 'Engagement keys',
    'Invoice #', 'Status', 'Issued', 'Due', 'Days past due',
    'Total', 'Services', 'Fees', 'Paid', 'Balance', 'Write-off', 'Credit applied',
    'Deposit applied',
  ]];
  for (const p of parents) {
    for (const c of p.clients) {
      const keys = c.engagements.map((e) => e.engagement_jira_issue_key).join(', ');
      const label = c.chargeover_display_name ?? c.tempo_account_name ?? c.tempo_account_id ?? '(unknown)';
      const invoices = [...c.engagements.flatMap((e) => e.invoices), ...c.orphan_invoices];
      for (const i of invoices) {
        rows.push([
          p.name, label, keys,
          i.invoice_number ?? i.chargeover_invoice_id,
          i.raw_status ?? '',
          i.date_issued ?? i.invoice_date ?? '',
          i.due_date ?? '',
          i.days_past_due ?? '',
          i.total_amount.toFixed(2),
          // Empty cell when line_items haven't been seen yet — distinguishes
          // "no fee" (0.00) from "we don't know yet" (blank) for finance.
          i.services_amount != null ? i.services_amount.toFixed(2) : '',
          i.fees_amount     != null ? i.fees_amount.toFixed(2)     : '',
          i.paid_amount.toFixed(2),
          i.balance_net.toFixed(2), i.writeoff_amount.toFixed(2),
          i.credit_amount.toFixed(2), i.deposit_applied.toFixed(2),
        ]);
      }
    }
  }
  downloadCsv('invoicing-report.csv', rows);
}

function ParentTable({ parents, filter, overdueBucket }: {
  parents: InvoiceReportParent[];
  filter: FilterId;
  overdueBucket: OverdueBucket;
}) {
  const { sorted, sort } = useTableSort(
    parents,
    {
      parent:       (p) => p.name,
      engagements:  (p) => p.clients.length,
      billed:       (p) => p.billed_total,
      paid:         (p) => p.paid_total,
      overdue:      (p) => p.overdue_balance,
      outstanding:  (p) => p.outstanding_balance,
      writeoff:     (p) => p.writeoff_total,
      credit:       (p) => p.credit_total,
      draft:        (p) => p.draft_total,
      deposit:      (p) => p.deposit_remaining,
      remaining:    (p) => p.credit_remaining,
      expected:     (p) => p.expected_to_collect,
    },
    { key: 'overdue', dir: 'desc' },
  );

  return (
    <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <Th> </Th>
            <SortableTh columnKey="parent"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Client</SortableTh>
            <SortableTh columnKey="engagements" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Engagements</SortableTh>
            <SortableTh columnKey="billed"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Billed</SortableTh>
            <SortableTh columnKey="paid"        sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Paid</SortableTh>
            <SortableTh columnKey="overdue"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Overdue</SortableTh>
            <SortableTh columnKey="outstanding" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Outstanding</SortableTh>
            <SortableTh columnKey="writeoff"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Write-off</SortableTh>
            <SortableTh columnKey="credit"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Credit</SortableTh>
            <SortableTh columnKey="draft"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Unapproved</SortableTh>
            <SortableTh columnKey="deposit"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Deposit remaining</SortableTh>
            <SortableTh columnKey="remaining"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Credit remaining</SortableTh>
            <SortableTh columnKey="expected"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Expected to collect</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => <ParentRow key={p.chargeover_customer_id} parent={p} filter={filter} overdueBucket={overdueBucket} />)}
        </tbody>
      </Table>
    </div>
  );
}

function ParentRow({ parent, filter, overdueBucket }: {
  parent: InvoiceReportParent;
  filter: FilterId;
  overdueBucket: OverdueBucket;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tr onClick={() => setOpen(!open)}>
        <Td muted style={{ fontSize: 11, width: 20 }}>{open ? '▼' : '▶'}</Td>
        <Td bold>
          {parent.name}
          {parent.synthetic && (
            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 400 }}>
              (no parent)
            </span>
          )}
        </Td>
        <Td right muted>{parent.clients.length}</Td>
        <Td right>{fmtCurrency(parent.billed_total)}</Td>
        <Td right muted>{fmtCurrency(parent.paid_total)}</Td>
        <Td right style={{ color: parent.overdue_balance > 0 ? 'var(--color-red-text)' : undefined, fontWeight: parent.overdue_balance > 0 ? 500 : undefined }}>
          {parent.overdue_balance > 0 ? fmtCurrency(parent.overdue_balance) : '—'}
        </Td>
        <Td right>{fmtCurrency(parent.outstanding_balance)}</Td>
        <Td right muted>{parent.writeoff_total > 0 ? fmtCurrency(parent.writeoff_total) : '—'}</Td>
        <Td right muted>{parent.credit_total > 0 ? fmtCurrency(parent.credit_total) : '—'}</Td>
        <Td right muted>{parent.draft_total > 0 ? fmtCurrency(parent.draft_total) : '—'}</Td>
        <Td right muted>{parent.deposit_remaining > 0 ? fmtCurrency(parent.deposit_remaining) : '—'}</Td>
        <Td right muted>{parent.credit_remaining > 0 ? fmtCurrency(parent.credit_remaining) : '—'}</Td>
        <Td right bold style={{ color: parent.expected_to_collect > 0 ? 'var(--color-blue)' : 'var(--color-text-muted)' }}>
          {parent.expected_to_collect > 0 ? fmtCurrency(parent.expected_to_collect) : '—'}
        </Td>
      </Tr>
      {open && parent.clients.map((c) => (
        <EngagementRow key={c.id} client={c} filter={filter} overdueBucket={overdueBucket} />
      ))}
    </>
  );
}

// "Engagement" row in UI terms = ChargeOver child customer = a single Tempo
// account. Typically one Jira engagement per child; keys shown inline.
// Expanding the row shows only invoices matching the active filter (so a
// client in the Overdue tab doesn't expose all their paid invoices too).
function EngagementRow({ client, filter, overdueBucket }: {
  client: InvoiceReportClient;
  filter: FilterId;
  overdueBucket: OverdueBucket;
}) {
  const [open, setOpen] = useState(false);
  const allInvoices: InvoiceReportInvoice[] = [
    ...client.engagements.flatMap((e) => e.invoices),
    ...client.orphan_invoices,
  ].filter((inv) => invoiceMatchesFilter(inv, filter, overdueBucket));
  return (
    <>
      <Tr onClick={() => setOpen(!open)}>
        <Td muted style={{ fontSize: 11, width: 20, paddingLeft: 28, background: 'var(--color-bg)' }}>{open ? '▼' : '▶'}</Td>
        <Td style={{ fontSize: 11 }}>
          <span>{client.chargeover_display_name ?? client.tempo_account_name ?? client.tempo_account_id ?? '(unknown)'}</span>
          {engagementKeys(client) && (
            <span style={{ marginLeft: 8, color: 'var(--color-text-muted)' }}>· {engagementKeys(client)}</span>
          )}
          {!client.has_external_key && (
            <span style={{ marginLeft: 8 }}>
              <Badge variant="amber">No external key</Badge>
            </span>
          )}
        </Td>
        <Td right muted style={{ fontSize: 11 }}>{allInvoices.length}</Td>
        <Td right style={{ fontSize: 11 }}>{fmtCurrency(client.billed_total)}</Td>
        <Td right muted style={{ fontSize: 11 }}>{fmtCurrency(client.paid_total)}</Td>
        <Td right style={{ fontSize: 11, color: client.overdue_balance > 0 ? 'var(--color-red-text)' : undefined, fontWeight: client.overdue_balance > 0 ? 500 : undefined }}>
          {client.overdue_balance > 0 ? fmtCurrency(client.overdue_balance) : '—'}
        </Td>
        <Td right style={{ fontSize: 11 }}>{fmtCurrency(client.outstanding_balance)}</Td>
        <Td right muted style={{ fontSize: 11 }}>{client.writeoff_total > 0 ? fmtCurrency(client.writeoff_total) : '—'}</Td>
        <Td right muted style={{ fontSize: 11 }}>{client.credit_total > 0 ? fmtCurrency(client.credit_total) : '—'}</Td>
        <Td right muted style={{ fontSize: 11 }}>{client.draft_total > 0 ? fmtCurrency(client.draft_total) : '—'}</Td>
        <Td right muted style={{ fontSize: 11 }}>{client.deposit_remaining > 0 ? fmtCurrency(client.deposit_remaining) : '—'}</Td>
        <Td right muted style={{ fontSize: 11 }}>{client.credit_remaining > 0 ? fmtCurrency(client.credit_remaining) : '—'}</Td>
        <Td right style={{ fontSize: 11, color: client.expected_to_collect > 0 ? 'var(--color-blue)' : 'var(--color-text-muted)' }}>
          {client.expected_to_collect > 0 ? fmtCurrency(client.expected_to_collect) : '—'}
        </Td>
      </Tr>
      {open && (
        <tr>
          <td colSpan={13} style={{ background: 'var(--color-bg)', padding: '8px 48px 16px' }}>
            {allInvoices.length > 0
              ? <InvoiceList invoices={allInvoices} />
              : <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>No invoices.</div>}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Side-by-side comparison of ORP's worklog list against ChargeOver's, so a
 * "ChargeOver disagrees" badge leads somewhere actionable instead of just
 * asserting that something is wrong.
 *
 * Two asymmetries the colouring has to convey, or the reader draws the wrong
 * conclusion:
 *   - ChargeOver-only rows are always real. That worklog WAS billed and ORP's
 *     reconstruction missed it.
 *   - ORP-only rows are only real when ChargeOver's list is complete. Against
 *     a clipped list they are unprovable, and shown greyed rather than red.
 */
const DIFF_PRESENTATION: Record<WorklogIdDiffRow['status'], {
  marker: string; label: string; title: string; tone: 'plain' | 'bad' | 'faint';
}> = {
  match: {
    marker: '=', label: 'Both',
    title: 'Present in ORP and in ChargeOver’s stored list',
    tone: 'plain',
  },
  only_chargeover: {
    marker: '−', label: 'Billed, not in ORP',
    title: 'ChargeOver billed this worklog on this line but ORP’s rebuild does not include it. Always a real conflict.',
    tone: 'bad',
  },
  only_orp: {
    marker: '+', label: 'In ORP, not billed',
    title: 'ORP includes this worklog but ChargeOver’s complete list does not.',
    tone: 'bad',
  },
  unprovable: {
    marker: '?', label: 'Beyond CO record',
    title: 'ChargeOver’s list hit its 245-character cap before reaching this worklog, so it can neither confirm nor deny it. Not a conflict.',
    tone: 'faint',
  },
};

/**
 * ORP vs ChargeOver for one invoice line, as a unified list with full worklog
 * detail on every row.
 *
 * Deliberately unified rather than split columns: the useful content is the
 * worklog itself — date, author, issue, hours, description — and duplicating
 * all of it across two half-width columns would repeat identical text on every
 * matching row while leaving no space for any of it. Matching, extra and
 * missing stay distinguishable through the marker and colour instead.
 */
function WorklogIdDiffView({ diff, config }: { diff: WorklogIdDiff; config: AppConfig | null }) {
  return (
    <div style={{ marginTop: 6, marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 10, marginBottom: 4 }}>
        <span style={{ color: 'var(--color-text-muted)' }}>{diff.matched} on both sides</span>
        {diff.only_chargeover > 0 && (
          <span style={{ color: 'var(--color-red-text)' }}>
            {diff.only_chargeover} billed but missing from ORP
          </span>
        )}
        {diff.only_orp > 0 && (
          <span style={{ color: 'var(--color-red-text)' }}>
            {diff.only_orp} in ORP but not billed
          </span>
        )}
        {diff.unprovable > 0 && (
          <span
            style={{ color: 'var(--color-text-hint)' }}
            title={DIFF_PRESENTATION.unprovable.title}
          >
            {diff.unprovable} beyond ChargeOver&rsquo;s record
          </span>
        )}
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '0.5px solid var(--color-border)' }}>
        <Table>
          <thead>
            <tr>
              <Th> </Th>
              <Th>Where</Th>
              <Th>Date</Th>
              <Th>Author</Th>
              {/* Named for its namespace, not "Issue". A Jira work item key and
                  a Tempo account key are identical in shape on a client's first
                  engagement (ACME-1 / ACME_01) and diverge after it, so the two
                  get read as typos of each other when diagnosing a diff. */}
              <Th>
                <span title="Jira work item key, e.g. ACME-1. NOT the Tempo account key (ACME_01) — the two coincide on a client's first engagement and diverge after that.">
                  Work item
                </span>
              </Th>
              <Th right>Hours</Th>
              <Th>Description</Th>
              <Th>Worklog ID</Th>
            </tr>
          </thead>
          <tbody>
            {diff.rows.map((r, idx) => {
              const p = DIFF_PRESENTATION[r.status];
              const w = r.worklog;
              const id = r.orp ?? r.chargeover ?? '';
              const issueRef = w?.jira_issue_key ?? w?.jira_issue_id ?? null;
              const href = jiraWorklogUrl(config, issueRef, w?.jira_worklog_id ?? null);
              const rowStyle =
                p.tone === 'bad' ? { background: 'var(--color-red-bg)' }
                : p.tone === 'faint' ? { color: 'var(--color-text-hint)' }
                : undefined;
              return (
                <Tr key={`${id}-${idx}`} style={rowStyle}>
                  <Td style={{ fontSize: 11, fontWeight: 700, width: 16 }}>
                    <span title={p.title}>{p.marker}</span>
                  </Td>
                  <Td style={{ fontSize: 10 }}><span title={p.title}>{p.label}</span></Td>
                  <Td muted style={{ fontSize: 11 }}>{w?.work_date ?? '—'}</Td>
                  <Td style={{ fontSize: 11 }}>{w?.author_display_name ?? '—'}</Td>
                  <Td style={{ fontSize: 11 }}>
                    {href && issueRef
                      ? <a href={href} target="_blank" rel="noopener noreferrer">{w?.jira_issue_key ?? `#${w?.jira_issue_id}`}</a>
                      : '—'}
                  </Td>
                  <Td right style={{ fontSize: 11 }}>{w ? w.billable_hours.toFixed(2) : '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>
                    {w?.description || '—'}
                    {/* The explanation, when there is one. A worklog ChargeOver
                        billed here usually still exists — it just moved. */}
                    {w && !w.in_orp && (
                      <span style={{ display: 'block', fontSize: 10, color: 'var(--color-red-text)' }}>
                        No longer in ORP — deleted from Tempo since this invoice was raised.
                      </span>
                    )}
                    {/* Leads with the namespace: this key sits beside a Jira
                        work item key in the row above it, and the bare value
                        alone reads as a mis-typed version of it. */}
                    {w?.in_orp && r.status === 'only_chargeover' && w.current_account_key && (
                      <span style={{ display: 'block', fontSize: 10, color: 'var(--color-amber-text)' }}>
                        Now under Tempo account {w.current_account_key}
                        {w.current_account_name && w.current_account_name !== w.current_account_key
                          ? ` — ${w.current_account_name}`
                          : ''}
                      </span>
                    )}
                  </Td>
                  <Td muted style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)' }}>{id}</Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/**
 * Worklogs behind one invoice, fetched on first expand.
 *
 * Lazy by design: the invoicing report renders hundreds of invoice rows, and
 * detail is only ever wanted for the one an operator is being asked about.
 */
function InvoiceWorklogPanel({ invoice }: { invoice: InvoiceReportInvoice }) {
  const { config } = useConfig();
  const [openDiffs, setOpenDiffs] = useState<Record<string, boolean>>({});
  const { data, isLoading, error } = useQuery(
    () => api.admin.invoiceWorklogs(invoice.chargeover_invoice_id),
    [invoice.chargeover_invoice_id],
    { cacheKey: `invoice-worklogs:${invoice.chargeover_invoice_id}` },
  );

  if (isLoading) return <div style={{ padding: 10 }}><Spinner /></div>;
  if (error) {
    return <div style={{ padding: 10, fontSize: 11, color: 'var(--color-red-text)' }}>{error}</div>;
  }

  const lines = data?.lines ?? [];
  if (lines.length === 0) {
    return (
      <div style={{ padding: 10, fontSize: 11, color: 'var(--color-text-muted)' }}>
        No worklog detail captured for this invoice. Invoices raised before this feature
        shipped need the reconstruction backfill (Admin → Billing).
      </div>
    );
  }

  const totalWorklogs = lines.reduce((s, l) => s + l.worklogs.length, 0);

  return (
    <div style={{ padding: '8px 10px', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {totalWorklogs} worklog{totalWorklogs === 1 ? '' : 's'} across {lines.length} weekly line{lines.length === 1 ? '' : 's'}
        </span>
        {/* Reconstructed lines are inferred from current worklog data, not a
            record of what was billed — say so rather than letting the numbers
            imply more certainty than they carry. */}
        {lines.some((l) => l.source === 'reconstructed') && (
          <span title="Rebuilt from current worklog data, not captured at invoice time">
            <Badge variant="amber">Reconstructed</Badge>
          </span>
        )}
        {lines.some((l) => l.verified_against_chargeover === false) && (
          <span title="ChargeOver's stored worklog list disagrees with this reconstruction">
            <Badge variant="red">ChargeOver mismatch</Badge>
          </span>
        )}
        <ActionButton
          variant="default"
          onClick={() => downloadInvoiceWorklogsCsv(invoice, lines)}
        >
          Export CSV
        </ActionButton>
      </div>

      {lines.map((line) => {
        // The invoice quantity is rounded UP to the nearest 15 min per week,
        // so quantity slightly exceeding the worklog sum is expected. A gap
        // the other way, or a large one, means worklogs are missing.
        const gap = line.line_quantity != null ? line.line_quantity - line.worklog_hours : null;
        const suspicious = gap != null && (gap < -0.01 || gap > 0.25);
        const lineKey = `${line.service_from_date}-${line.service_to_date}`;
        const diff = line.chargeover_diff;
        // Open conflicting lines by default — a badge nobody expands is no
        // better than the boolean it replaced.
        const diffOpen = openDiffs[lineKey] ?? diff?.has_conflict ?? false;
        return (
          <div key={lineKey}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>
                {line.service_from_date} → {line.service_to_date}
              </span>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                {line.worklog_hours.toFixed(2)}h logged
                {line.line_quantity != null && ` · ${line.line_quantity.toFixed(2)}h billed`}
              </span>
              {suspicious && (
                <span
                  style={{ fontSize: 10, color: 'var(--color-red-text)' }}
                  title="Billed quantity doesn't line up with the captured worklogs — some may be missing"
                >
                  ({gap! > 0 ? '+' : ''}{gap!.toFixed(2)}h unaccounted)
                </span>
              )}
              {diff && (
                <button
                  onClick={() => setOpenDiffs((s) => ({ ...s, [lineKey]: !diffOpen }))}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 10, textDecoration: 'underline',
                    color: diff.has_conflict ? 'var(--color-red-text)' : 'var(--color-text-muted)',
                  }}
                >
                  {diffOpen ? 'Hide' : 'Show'} ChargeOver comparison
                  {diff.has_conflict && ` (${diff.only_chargeover + diff.only_orp} differ)`}
                </button>
              )}
            </div>
            {diff && diffOpen && <WorklogIdDiffView diff={diff} config={config} />}
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Author</Th>
                  <Th>Issue</Th>
                  <Th right>Hours</Th>
                  <Th>Description</Th>
                  {/* The ID ops needs when they do have to go to Tempo. */}
                  <Th>Worklog ID</Th>
                </tr>
              </thead>
              <tbody>
                {line.worklogs.map((w) => {
                  const issueRef = w.jira_issue_key ?? w.jira_issue_id;
                  const href = jiraWorklogUrl(config, issueRef, w.jira_worklog_id);
                  return (
                    <Tr key={w.tempo_worklog_id}>
                      <Td muted style={{ fontSize: 11 }}>{w.work_date ?? '—'}</Td>
                      <Td style={{ fontSize: 11 }}>
                        {w.author_display_name ?? w.author_jira_user_id ?? '—'}
                      </Td>
                      <Td style={{ fontSize: 11 }}>
                        {href && issueRef ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={w.jira_worklog_id
                              ? 'Open this worklog in Jira'
                              : 'Open the issue in Jira — the exact worklog is not linkable (captured before worklog ids were synced, or deleted from Tempo)'}
                          >
                            {w.jira_issue_key ?? `#${w.jira_issue_id}`}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--color-text-hint)' }}>—</span>
                        )}
                      </Td>
                      <Td right style={{ fontSize: 11 }}>{w.billable_hours.toFixed(2)}</Td>
                      <Td muted style={{ fontSize: 11 }}>{w.description || '—'}</Td>
                      <Td muted style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)' }}>
                        {w.tempo_worklog_id}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        );
      })}
    </div>
  );
}

/** Flatten to one row per worklog, repeating the line window on each so the
 *  file stands alone when pasted into a client email. Columns match the
 *  server's `?format=csv` rendering of the same endpoint. */
function downloadInvoiceWorklogsCsv(invoice: InvoiceReportInvoice, lines: InvoiceWorklogLine[]): void {
  const rows: Array<Array<unknown>> = [
    ['Week start', 'Week end', 'Work date', 'Author', 'Jira issue', 'Billable hours', 'Description', 'Tempo worklog ID', 'Source'],
  ];
  for (const line of lines) {
    for (const w of line.worklogs) {
      rows.push([
        line.service_from_date,
        line.service_to_date,
        w.work_date ?? '',
        w.author_display_name ?? w.author_jira_user_id ?? '',
        w.jira_issue_key ?? w.jira_issue_id ?? '',
        w.billable_hours,
        w.description ?? '',
        w.tempo_worklog_id,
        line.source,
      ]);
    }
  }
  downloadCsv(`invoice-${invoice.invoice_number ?? invoice.chargeover_invoice_id}-worklogs.csv`, rows);
}

/** One invoice row plus its lazily-loaded worklog detail. */
function InvoiceRow({ invoice }: { invoice: InvoiceReportInvoice }) {
  const [open, setOpen] = useState(false);
  const i = invoice;
  return (
    <>
      <Tr>
        <Td bold>
          {/* The disclosure is its own control rather than a row click: the
              row already carries invoice and PDF links, and swallowing
              clicks on those to toggle would be worse than an explicit
              caret. */}
          <button
            onClick={() => setOpen(!open)}
            title={open ? 'Hide worklogs' : 'Show the worklogs behind this invoice'}
            aria-expanded={open}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 4px 0 0',
              color: 'var(--color-text-muted)',
              fontSize: 10,
            }}
          >
            {open ? '▾' : '▸'}
          </button>
          <InvoiceLink
            invoiceId={i.chargeover_invoice_id}
            display={i.invoice_number ?? i.chargeover_invoice_id}
          />
          {/* Direct PDF link — token-gated CO portal URL, no admin
              login required. Sits inline as a tiny "PDF" chip. */}
          {i.url_pdflink && (
            <a
              href={i.url_pdflink}
              target="_blank"
              rel="noopener noreferrer"
              title="Open invoice PDF"
              style={{
                marginLeft: 6,
                fontSize: 9,
                fontWeight: 500,
                textDecoration: 'none',
                color: 'var(--color-text-muted)',
                padding: '1px 5px',
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 3,
                verticalAlign: 'middle',
              }}
            >
              PDF
            </a>
          )}
          {i.lifecycle === 'draft' && (
            <CreditAvailableBadge
              unappliedCredit={i.customer_unapplied_credit}
              pendingDeposit={i.customer_pending_deposit}
            />
          )}
          {/* Visible outside the mismatch filter too, so the discrepancy is
              apparent when browsing rather than only when hunting for it. */}
          {i.has_chargeover_discrepancy && (
            <span
              title="ChargeOver's stored worklog list disagrees with ORP's — expand to see which worklogs"
              style={{ marginLeft: 6 }}
            >
              <Badge variant="red">CO mismatch</Badge>
            </span>
          )}
        </Td>
        <Td><Badge variant={statusBadgeVariant(i.raw_status)}>{i.raw_status || 'Unknown'}</Badge></Td>
        <Td muted style={{ fontSize: 11 }}>{i.date_issued ?? i.invoice_date ?? '—'}</Td>
        <Td muted style={{ fontSize: 11 }}>
          {i.due_date ?? '—'}
          {i.days_past_due != null && i.days_past_due > 0 && (
            <span style={{ marginLeft: 6, color: 'var(--color-red-text)', fontSize: 10 }}>
              +{i.days_past_due}d
            </span>
          )}
        </Td>
        <Td right>{fmtCurrency(i.total_amount)}</Td>
        {/* services_amount / fees_amount come from the line-items
            breakdown — null until the backfill probes the invoice via
            /invoice/{id}?_meta_fields=*. Show "—" with a tooltip in
            the not-yet-known case so the operator can tell "no fee"
            apart from "we don't know yet". */}
        <Td right>
          {i.services_amount != null
            ? fmtCurrency(i.services_amount)
            : <span style={{ color: 'var(--color-text-hint)' }} title="Line items not yet synced">—</span>}
        </Td>
        <Td right muted>
          {i.fees_amount == null
            ? <span style={{ color: 'var(--color-text-hint)' }} title="Line items not yet synced">—</span>
            : i.fees_amount > 0 ? fmtCurrency(i.fees_amount) : '—'}
        </Td>
        <Td right muted>{fmtCurrency(i.paid_amount)}</Td>
        <Td right muted>{i.writeoff_amount > 0 ? fmtCurrency(i.writeoff_amount) : '—'}</Td>
        <Td right muted>{i.credit_amount > 0 ? fmtCurrency(i.credit_amount) : '—'}</Td>
        {/* The collectable balance, not ChargeOver's gross one. A deposit
            drawdown never moves balance_amount, so showing that would put a
            bold red figure on a row the client total counts as settled. */}
        <Td right bold style={{ color: i.balance_net > 0 && i.days_past_due != null && i.days_past_due > 0 ? 'var(--color-red-text)' : i.balance_net > 0 ? undefined : 'var(--color-text-muted)' }}>
          {fmtCurrency(i.balance_net)}
          {i.deposit_applied > 0 && (
            <span
              style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'var(--color-text-muted)' }}
              title="Settled from this customer's deposit — ChargeOver records the drawdown on the transaction, not on the invoice"
            >
              {fmtCurrency(i.deposit_applied)} from deposit
            </span>
          )}
          {/* Without this the row shows $0 while ChargeOver shows a balance,
              which reads as ORP losing money rather than deferring to CO. */}
          {i.settled_off_invoice > 0 && (
            <span
              style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'var(--color-text-muted)' }}
              title={`ChargeOver marks this invoice paid but still reports ${fmtCurrency(i.balance_amount)} of balance against it. Its status is the authority on what is collectable, so ORP treats the difference as settled.`}
            >
              {fmtCurrency(i.settled_off_invoice)} settled off-invoice
            </span>
          )}
        </Td>
      </Tr>
      {open && (
        <tr>
          <td colSpan={11} style={{ background: 'var(--color-bg-subtle)', padding: 0 }}>
            <InvoiceWorklogPanel invoice={i} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoiceList({ invoices }: { invoices: InvoiceReportInvoice[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Invoice #</Th>
          <Th>Status</Th>
          <Th>Issued</Th>
          <Th>Due</Th>
          <Th right>Total</Th>
          {/* "Our component" — total minus CC processing / convenience fees. */}
          <Th right>Services</Th>
          <Th right>Fees</Th>
          <Th right>Paid</Th>
          <Th right>Write-off</Th>
          <Th right>Credit</Th>
          <Th right>Balance</Th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((i) => (
          <InvoiceRow key={i.chargeover_invoice_id} invoice={i} />
        ))}
      </tbody>
    </Table>
  );
}
